/**
 * Sesión B WF1 — Client creation request dialog (advisor surface).
 * The advisor fills the form and POSTs to /admin/client-creation-requests.
 * The actual client/company is NOT created until hm_admin approves.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { requestsApi } from '@/lib/adminApi';

const SUITE_CODES = [
  'talentmap', 'talentcheck', 'talentstart', 'talentheart', 'talentfix',
  'climara', 'talentcompetency', 'talenttrain', 'intelligence',
];

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

export default function ClientRequestDialog({ open, onOpenChange, onSubmitted }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company_name: '', country: '', industry: '',
    contact_name: '', contact_email: '',
    employees_count: 1, requested_suites: [], notes: '',
    priority: 'medium',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSuite = (code) => setForm((f) => ({
    ...f,
    requested_suites: f.requested_suites.includes(code)
      ? f.requested_suites.filter((c) => c !== code)
      : [...f.requested_suites, code],
  }));

  const submit = async () => {
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error(t('pages.requests.clientForm.validationMissing'));
      return;
    }
    setSaving(true);
    try {
      const doc = await requestsApi.createClientRequest({
        ...form,
        employees_count: Number(form.employees_count) || 0,
      });
      toast.success(t('pages.requests.clientForm.submitted'));
      onSubmitted?.(doc);
      onOpenChange(false);
      setForm({
        company_name: '', country: '', industry: '',
        contact_name: '', contact_email: '',
        employees_count: 1, requested_suites: [], notes: '',
        priority: 'medium',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pages.requests.clientForm.submitFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="client-request-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.requests.clientForm.title')}</DialogTitle>
          <DialogDescription>{t('pages.requests.clientForm.description')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <div className="md:col-span-2">
            <Label>{t('pages.requests.clientForm.companyName')} *</Label>
            <Input
              value={form.company_name}
              onChange={(e) => set('company_name', e.target.value)}
              data-testid="client-req-company-name"
            />
          </div>
          <div>
            <Label>{t('pages.requests.clientForm.country')}</Label>
            <Input value={form.country} onChange={(e) => set('country', e.target.value)} data-testid="client-req-country" />
          </div>
          <div>
            <Label>{t('pages.requests.clientForm.industry')}</Label>
            <Input value={form.industry} onChange={(e) => set('industry', e.target.value)} data-testid="client-req-industry" />
          </div>
          <div>
            <Label>{t('pages.requests.clientForm.contactName')} *</Label>
            <Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} data-testid="client-req-contact-name" />
          </div>
          <div>
            <Label>{t('pages.requests.clientForm.contactEmail')} *</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} data-testid="client-req-contact-email" />
          </div>
          <div>
            <Label>{t('pages.requests.clientForm.employees')}</Label>
            <Input type="number" min="0" value={form.employees_count} onChange={(e) => set('employees_count', e.target.value)} data-testid="client-req-employees" />
          </div>
          <div>
            <Label>{t('pages.requests.clientForm.priority')}</Label>
            <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
              <SelectTrigger data-testid="client-req-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>{t(`pages.requests.priority.${p}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>{t('pages.requests.clientForm.requestedSuites')}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1 p-3 rounded-md border bg-muted/20">
              {SUITE_CODES.map((code) => (
                <label key={code} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.requested_suites.includes(code)}
                    onCheckedChange={() => toggleSuite(code)}
                    data-testid={`client-req-suite-${code}`}
                  />
                  <span>{t(`suite.names.${code}`, { defaultValue: code })}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>{t('pages.requests.clientForm.notes')}</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              data-testid="client-req-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-req-cancel">
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="client-req-submit">
            {saving ? t('common.saving') : t('pages.requests.clientForm.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
